import { defineStore } from 'pinia'
import axios from 'axios'

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,
    isAuthenticated: false,
    token: (() => {
      try {
        return localStorage.getItem('userToken') || sessionStorage.getItem('userToken') || null;
      } catch {
        return null;
      }
    })()
  }),

  getters: {
    getUser: (state) => state.user,
    isAdmin: (state) => !!state.user?.isAdmin || state.user?.role === 'admin',
    hasRole: (state) => (roles) => roles.includes(state.user?.role),
    
    // Получить все разрешения пользователя
    permissions: (state) => state.user?.permissions || [],
    
    // Проверка наличия разрешения на страницу
    hasPermission: (state) => (pageName, permissionName) => {
      if (!state.user || !state.user.permissions) return false
      // Админ имеет все разрешения
      if (state.user.isAdmin || state.user.role === 'admin') return true
      // Проверяем наличие разрешения в массиве
      return state.user.permissions.some(
        perm => perm.pageName === pageName && perm.permissionName === permissionName
      )
    },
    
    // Проверка доступа к админке (view на admin_panel)
    hasAdminAccess(state) {
      if (!state.user) return false
      if (state.user.isAdmin || state.user.role === 'admin') return true
      // Проверяем наличие разрешения напрямую
      if (!state.user.permissions) return false
      return state.user.permissions.some(
        perm => perm.pageName === 'admin_panel' && perm.permissionName === 'view'
      )
    },
    
    // Проверка доступа к БД через разрешения
    hasDbAccess(state) {
      if (!state.user) return false
      // Админ имеет полный доступ
      if (state.user.isAdmin || state.user.role === 'admin') return true
      // Проверяем доступ к админке (нужно для отображения раздела БД)
      if (!state.user.permissions) return false
      return state.user.permissions.some(
        perm => perm.pageName === 'admin_panel' && perm.permissionName === 'view'
      )
    },
    
    // Проверка доступа к созданию пользователей (edit на admin_panel)
    canManageUsers(state) {
      if (!state.user) return false
      if (state.user.isAdmin || state.user.role === 'admin') return true
      if (!state.user.permissions) return false
      return state.user.permissions.some(
        perm => perm.pageName === 'admin_panel' && perm.permissionName === 'edit'
      )
    },
    
    // Проверка доступа к Google Access (edit на admin_panel)
    canManageGoogleAccess(state) {
      if (!state.user) return false
      if (state.user.isAdmin || state.user.role === 'admin') return true
      if (!state.user.permissions) return false
      return state.user.permissions.some(
        perm => perm.pageName === 'admin_panel' && perm.permissionName === 'edit'
      )
    }
  },

  actions: {
    async login(username, password) {
      try {
        const response = await axios.post('https://ncht.bz/api/auth/login', {
          username,
          password
        });

        const { token, user } = response.data;
        this.setToken(token);
        this.setUser(user);
        return true;
      } catch (error) {
        console.error('Ошибка при входе:', error);
        this.logout();
        throw error;
      }
    },

    async checkAuth() {
      if (!this.token) {
        this.isAuthenticated = false;
        return false;
      }

      try {
        const response = await axios.get('https://ncht.bz/api/auth/authorization', {
          headers: {
            'Authorization': `Bearer ${this.token}`
          }
        });
        
        if (response.status === 200) {
          this.setUser(response.data);
          return true;
        }
        
        return false;
      } catch (error) {
        console.error("Ошибка при проверке авторизации:", error);
        this.logout();
        return false;
      }
    },

    setUser(userData) {
      this.user = userData;
      this.isAuthenticated = true;
    },

    setToken(token) {
      if (!token) return;
      this.token = token;
      try {
        localStorage.setItem('userToken', token);
      } catch (e) {
        console.warn('localStorage.setItem failed:', e);
      }
      try {
        sessionStorage.setItem('userToken', token);
      } catch (e) {
        console.warn('sessionStorage.setItem failed:', e);
      }
    },

    logout() {
      this.user = null;
      this.token = null;
      this.isAuthenticated = false;
      try {
        localStorage.removeItem('userToken');
        sessionStorage.removeItem('userToken');
      } catch (e) {
        console.warn('Failed to clear auth token from storage:', e);
      }
    }
  }
}) 